import React, { useState, useContext, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useBackNavigation } from "../navigation/BackNavigationContext";
import { UserContext } from "../utils/userContext";
import {
  canAccessAdminMenu,
  isSuperAdmin,
  isCustomerAdmin,
  isOrganisationAdminRole,
  CUSTOMER_ROLES,
} from "../utils/roles";
import { useT } from "../contexts/TranslationContext";
import axiosInstance from "../utils/axiosInstance";
import LogoImg from "../assets/LM_Logo_vit.png";
import LanguageModal from "./LanguageModal";

const TopBar = () => {
  const { user } = useContext(UserContext);
  const { t } = useT();
  const navigate = useNavigate();
  const { clearStack } = useBackNavigation();

  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [isCompanyMenuOpen, setIsCompanyMenuOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [openFlagCount, setOpenFlagCount] = useState(0);

  useEffect(() => {
    if (!user?.userId) return;
    axiosInstance
      .get("/auth/companies")
      .then((res) => setCompanies(res.data.companies || []))
      .catch(() => setCompanies([]));
  }, [user?.userId, user?.activeCompanyId]);

  // Unresolved follow-up items: the badge stays until the work is actually done, not
  // just until an admin has looked. Polled rather than pushed; there is no websocket layer.
  useEffect(() => {
    if (!canAccessAdminMenu(user)) {
      setOpenFlagCount(0);
      return undefined;
    }
    let cancelled = false;
    const load = () =>
      axiosInstance
        .get("/admin/report-flags/summary")
        .then((res) => {
          if (!cancelled) setOpenFlagCount(res.data?.openTotal || 0);
        })
        .catch(() => {
          if (!cancelled) setOpenFlagCount(0);
        });
    load();
    const interval = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId, user?.activeCompanyId]);

  const handleSwitchCompany = async (companyId) => {
    try {
      const res = await axiosInstance.post("/auth/switch-company", { companyId });
      localStorage.setItem("token", res.data.accessToken);
      localStorage.setItem("refreshToken", res.data.refreshToken);
      window.dispatchEvent(new Event("tokenUpdated"));
      setIsCompanyMenuOpen(false);
      window.location.href = "/report";
    } catch (error) {
      console.error("Failed to switch company:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await axiosInstance.post("/auth/logout", { userId: user?._id });
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleLanguageChange = async (newLang) => {
    if (!newLang || newLang === (user?.languages?.ui || "English")) return;
    try {
      await axiosInstance.patch("/users/me", { uiLanguage: newLang });
      const refreshToken = localStorage.getItem("refreshToken");
      if (refreshToken) {
        const res = await axios.post("/api/auth/refresh-token", { refreshToken });
        localStorage.setItem("token", res.data.accessToken);
        localStorage.setItem("refreshToken", res.data.refreshToken);
        window.dispatchEvent(new Event("tokenUpdated"));
      }
    } catch (error) {
      console.error("Failed to update language:", error);
    }
    setIsProfileMenuOpen(false);
    setIsMobileMenuOpen(false);
    setIsLanguageModalOpen(false);
  };

  const initials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  return (
    <>
      <nav className="bg-blue-600 text-white w-full z-50 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-14 flex items-center justify-between">
            {/* Left side: Logo + Desktop Links */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  // Clear the in-app back stack and navigate to the report as a replacement
                  // so the user cannot go back to the previous view.
                  clearStack();
                  navigate(user ? "/report" : "/login", { replace: true });
                }}
                className="flex items-center focus:outline-none"
              >
                <img
                  src={LogoImg}
                  alt={t("topBar.logoAlt")}
                  className="h-8 w-auto object-contain"
                />
              </button>

              {/* Desktop nav - hidden on small screens */}
              <div className="hidden md:flex items-center space-x-4 ml-4">
                <Link
                  to="/report"
                  className="hover:underline text-sm font-medium"
                >
                  {t("topBar.timeReport")}
                </Link>
                <Link
                  to="/projects"
                  className="hover:underline text-sm font-medium"
                >
                  {t("topBar.projects")}
                </Link>

                {companies.length > 1 && (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setIsCompanyMenuOpen((s) => !s)}
                      className="text-sm font-medium inline-flex items-center focus:outline-none border border-white/40 rounded px-2 py-0.5"
                    >
                      Company
                      <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isCompanyMenuOpen && (
                      <div className="absolute left-0 mt-2 w-48 bg-white text-gray-800 rounded shadow-lg py-1 z-50 max-h-64 overflow-y-auto">
                        {companies.map((c) => (
                          <button
                            key={c._id}
                            type="button"
                            onClick={() => handleSwitchCompany(c._id)}
                            className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                              String(c._id) === String(user?.activeCompanyId)
                                ? "font-semibold bg-gray-50"
                                : ""
                            }`}
                          >
                            {c.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {canAccessAdminMenu(user) && (
                  <div className="relative">
                    <button
                      onClick={() => setIsAdminMenuOpen((s) => !s)}
                      className="text-sm font-medium inline-flex items-center focus:outline-none"
                      aria-haspopup="true"
                      aria-expanded={isAdminMenuOpen}
                    >
                      {t("topBar.admin")}
                      {openFlagCount > 0 && (
                        <span className="ml-1.5 h-2 w-2 rounded-full bg-red-400" aria-hidden />
                      )}
                      <svg
                        className="ml-1 h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {isAdminMenuOpen && (
                      <div className="absolute left-0 mt-2 w-44 bg-white text-gray-800 rounded shadow-lg py-1 z-50">
                        {isSuperAdmin(user) && (
                          <Link
                            to="/admin/customers"
                            className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                            onClick={() => setIsAdminMenuOpen(false)}
                          >
                            Customers
                          </Link>
                        )}
                        {isCustomerAdmin(user) && !isSuperAdmin(user) && user?.activeCustomerId && (
                          <Link
                            to={`/admin/customers/${user.activeCustomerId}`}
                            className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                            onClick={() => setIsAdminMenuOpen(false)}
                          >
                            Customers
                          </Link>
                        )}
                        {!isSuperAdmin(user) &&
                          (user?.customerRole === CUSTOMER_ROLES.CUSTOMER_ADMIN ||
                            isOrganisationAdminRole(
                              user?.organisationRole || user?.companyRole
                            )) && (
                          <Link
                            to="/admin/organisations"
                            className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                            onClick={() => setIsAdminMenuOpen(false)}
                          >
                            Departments
                          </Link>
                        )}
                        <Link
                          to="/admin/follow-up"
                          className="flex items-center justify-between w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                          onClick={() => setIsAdminMenuOpen(false)}
                        >
                          <span>{t("topBar.followUp")}</span>
                          {openFlagCount > 0 && (
                            <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-red-600 text-white text-xs">
                              {openFlagCount}
                            </span>
                          )}
                        </Link>
                        <Link
                          to="/admin/task-registry"
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                          onClick={() => setIsAdminMenuOpen(false)}
                        >
                          {t("topBar.taskRegistry")}
                        </Link>
                        <Link
                          to="/admin/invoices"
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                          onClick={() => setIsAdminMenuOpen(false)}
                        >
                          {t("topBar.supplierInvoices")}
                        </Link>
                        <Link
                          to="/admin/billing"
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                          onClick={() => setIsAdminMenuOpen(false)}
                        >
                          {t("topBar.billing")}
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right side: profile & mobile menu button */}
            <div className="flex items-center space-x-4">
              {/* Desktop profile */}
              <div className="hidden md:flex items-center relative">
                <button
                  onClick={() => setIsProfileMenuOpen((s) => !s)}
                  className="flex items-center justify-center h-9 w-9 rounded-full bg-white text-blue-600 font-semibold focus:outline-none"
                  aria-haspopup="true"
                  aria-expanded={isProfileMenuOpen}
                >
                  {/* If you have user image, replace with <img /> */}
                  <span>{initials(user?.name)}</span>
                </button>

                {isProfileMenuOpen && (
                  <div className="absolute right-0 mt-12 w-48 bg-white text-gray-800 rounded shadow-lg py-1 z-50">
                    <button
                      onClick={() => {
                        setIsProfileMenuOpen(false);
                        setIsLanguageModalOpen(true);
                      }}
                      className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                    >
                      <svg
                        className="h-4 w-4 text-gray-500 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0h.5a2.5 2.5 0 002.5-2.5V8m0 0V3.935M12 21a9 9 0 100-18 9 9 0 000 18z"
                        />
                      </svg>
                      {t("topBar.language")}
                    </button>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                    >
                      <svg
                        className="h-4 w-4 text-gray-500 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                      {t("topBar.logOut")}
                    </button>
                  </div>
                )}
              </div>

              {/* Mobile menu button */}
              <div className="md:hidden flex items-center">
                <button
                  onClick={() => setIsMobileMenuOpen((s) => !s)}
                  aria-label={t("topBar.openMenu")}
                  className="p-2 rounded-md focus:outline-none"
                >
                  <svg
                    className="h-6 w-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    {isMobileMenuOpen ? (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    ) : (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 6h16M4 12h16M4 18h16"
                      />
                    )}
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile menu - shown when hamburger is active */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-blue-600 border-t border-blue-500">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link
                to="/report"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-blue-700"
              >
                {t("topBar.timeReport")}
              </Link>
              <Link
                to="/projects"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-blue-700"
              >
                {t("topBar.projects")}
              </Link>

              {canAccessAdminMenu(user) && (
                <div className="border-t border-blue-500 pt-2">
                  {isSuperAdmin(user) && (
                    <Link
                      to="/admin/customers"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-blue-700"
                    >
                      Customers
                    </Link>
                  )}
                  {isCustomerAdmin(user) && !isSuperAdmin(user) && user?.activeCustomerId && (
                    <Link
                      to={`/admin/customers/${user.activeCustomerId}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-blue-700"
                    >
                      Customers
                    </Link>
                  )}
                  {!isSuperAdmin(user) &&
                    (user?.customerRole === CUSTOMER_ROLES.CUSTOMER_ADMIN ||
                      isOrganisationAdminRole(
                        user?.organisationRole || user?.companyRole
                      )) && (
                    <Link
                      to="/admin/organisations"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-blue-700"
                    >
                      Departments
                    </Link>
                  )}
                  <Link
                    to="/admin/follow-up"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center justify-between px-3 py-2 rounded-md text-base font-medium text-white hover:bg-blue-700"
                  >
                    <span>{t("topBar.followUp")}</span>
                    {openFlagCount > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-red-600 text-white text-xs">
                        {openFlagCount}
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/admin/task-registry"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-blue-700"
                  >
                    {t("topBar.taskRegistry")}
                  </Link>
                  <Link
                    to="/admin/invoices"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-blue-700"
                  >
                    {t("topBar.supplierInvoices")}
                  </Link>
                  <Link
                    to="/admin/billing"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-blue-700"
                  >
                    {t("topBar.billing")}
                  </Link>
                </div>
              )}

              <div className="border-t border-blue-500 pt-2">
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setIsLanguageModalOpen(true);
                  }}
                  className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-md text-base font-medium text-white hover:bg-blue-700"
                >
                  <svg
                    className="h-5 w-5 text-white shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0h.5a2.5 2.5 0 002.5-2.5V8m0 0V3.935M12 21a9 9 0 100-18 9 9 0 000 18z"
                    />
                  </svg>
                  {t("topBar.language")}
                </button>
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    handleLogout();
                  }}
                  className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-md text-base font-medium text-white hover:bg-blue-700"
                >
                  <svg
                    className="h-5 w-5 text-white shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  {t("topBar.logOut")}
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      <LanguageModal
        isOpen={isLanguageModalOpen}
        onClose={() => setIsLanguageModalOpen(false)}
        currentLanguage={user?.languages?.ui || "English"}
        onSelectLanguage={handleLanguageChange}
      />
    </>
  );
};

export default TopBar;
